import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select, { components } from "react-select";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { exportToBrandedExcel, exportToExcel } from "../../utils/exportUtils";
import {
  getAssetCategories,
} from "../../services/ValitanApiService";
import {
  applyReplacementPricing,
  exportReplacementPricingUsersDaily,
  exportReplacementPricingNamedColumns,
  getReplacementPricingFilterOptions,
  getReplacementPricingAssets,
  getReplacementPricingProgress,
} from "../../services/PricingService";
import tdLogo from "../../assets/images/TD-letterhead-2026-1.png";
import "./PricingDashboard.css";

const PAGE_SIZE_OPTIONS = [
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
];

const RESULT_CONTEXTS = [
  { value: "unpriced", label: "Unpriced", tone: "warning" },
  { value: "priced", label: "Priced", tone: "success" },
];

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <div className="d-flex align-items-center gap-2">
      <input type="checkbox" checked={props.isSelected} readOnly />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

const parseListPayload = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const parsePagePayload = (response) => response?.data?.data ?? {};

const resolveBlobErrorMessage = async (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (text && text.trim()) {
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed?.message === "string" && parsed.message.trim()) {
            return parsed.message;
          }
        } catch (jsonParseError) {
          const compactText = text.replace(/\s+/g, " ").trim();
          if (compactText) {
            return compactText.slice(0, 220);
          }
        }
      }
    } catch (parseError) {
      // Ignore parse failures and keep fallback message.
    }
  }

  const status = error?.response?.status;
  const statusText = error?.response?.statusText;
  const responseMessage = error?.response?.data?.message;
  const directMessage = error?.message;
  return responseMessage || directMessage || (status ? `${fallbackMessage} (HTTP ${status}${statusText ? ` ${statusText}` : ""})` : fallbackMessage);
};

const resolveAssetId = (item) => String(
  item?.assetId ??
  item?.id ??
  item?.asset_id ??
  ""
).trim();

const normalizePricingItem = (item) => {
  const normalized = item && typeof item === "object" ? item : {};
  const assetId = resolveAssetId(normalized);
  const rowId = assetId || String(
    normalized.barcode ??
    normalized.display_name ??
    normalized.displayName ??
    normalized.name ??
    Math.random()
  ).trim();

  return {
    ...normalized,
    assetId,
    rowId,
  };
};

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return "-";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    maximumFractionDigits: 0,
  }).format(number);
};

const getUnitCount = (item) => Number(item?.unit_count ?? item?.unitCount ?? item?.quantity ?? 1) || 1;

const getOriginalReplacementCost = (item) =>
  Number(
    item?.original_replacement_cost ??
    item?.originalReplacementCost ??
    item?.current_replacement_cost ??
    item?.currentReplacementCost ??
    0
  ) || 0;

const getUnitDepreciated = (item) =>
  Number(
    item?.unit_depreciated ??
    item?.unitDepreciated ??
    item?.accumulated_depreciation_amount ??
    item?.accumulatedDepreciationAmount ??
    0
  ) || 0;

const getUnitDepreciatedPrice = (item) => {
  const currentReplacementCost = getOriginalReplacementCost(item);
  const unitDepreciated = getUnitDepreciated(item);
  return Math.max(currentReplacementCost - unitDepreciated, 0);
};

const formatThousands = (value) => {
  const digitsOnly = String(value ?? "").replace(/[^\d]/g, "");
  if (!digitsOnly) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Number(digitsOnly));
};

const toUpperLabel = (value) => String(value ?? "").trim().toUpperCase();

const toTitleCaseLabel = (value) => String(value ?? "")
  .trim()
  .toLowerCase()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getAssetValue = (item, snakeKey, camelKey = snakeKey) => item?.[snakeKey] ?? item?.[camelKey] ?? "";

const getFirstPhotoUrl = (item) => getAssetValue(item, "first_photo_url", "firstPhotoUrl");

const getPhotoUrls = (item) => {
  const raw = item?.photo_urls ?? item?.photoUrls;
  const normalize = (value) => String(value ?? "").trim();

  if (Array.isArray(raw)) {
    const urls = [...new Set(raw.map(normalize).filter(Boolean))];
    if (urls.length > 0) return urls;
  } else if (typeof raw === "string" && raw.trim()) {
    return [raw.trim()];
  }

  const first = normalize(getFirstPhotoUrl(item));
  return first ? [first] : [];
};

const getAssetPhotoAlt = (item, index) =>
  `${item.display_name || item.displayName || item.name || "Asset"} view ${index + 1}`;

const normalizeExportValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value;
};

const hasExportValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const downloadBlobResponse = (response, fallbackName) => {
  const blob = response?.data;
  if (!blob) throw new Error("Export file was empty.");

  const contentDisposition = response?.headers?.["content-disposition"] || "";
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = filenameMatch?.[1] || fallbackName;

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

const ASSET_EXPORT_COLUMNS = [
  { key: "barcode", label: "Barcode", value: (item) => item.barcode },
  { key: "category_code", label: "Category Code", value: (item) => getAssetValue(item, "category_code", "categoryCode") },
  { key: "category_name", label: "Asset Category", value: (item) => getAssetValue(item, "category_name", "categoryName") },
  { key: "normalized_asset_name", label: "Asset Name", value: (item) => item.display_name || item.displayName || item.name },
  { key: "other_name", label: "Other Name", value: (item) => getAssetValue(item, "other_name", "otherName") },
  { key: "description", label: "Description", value: (item) => item.description },
  { key: "remark", label: "Remark", value: (item) => item.remark },
  { key: "condition_name", label: "Condition", value: (item) => getAssetValue(item, "condition_name", "conditionName") },
  { key: "observed_condition", label: "Observed Condition Raw", value: (item) => getAssetValue(item, "observed_condition", "observedCondition") },
  { key: "quantity", label: "Quantity", value: (item) => item.quantity },
  { key: "number_of_buildings", label: "Number of Buildings", value: (item) => getAssetValue(item, "number_of_buildings", "numberOfBuildings") },
  { key: "unit_count", label: "Unit Count", value: (item) => getAssetValue(item, "unit_count", "unitCount") },
  { key: "pricing_user_name", label: "Priced By", value: (item) => getAssetValue(item, "pricing_user_name", "pricingUserName") },
  { key: "current_replacement_cost", label: "Unit Replacement Cost (TZS)", value: (item) => getOriginalReplacementCost(item) },
  { key: "original_replacement_cost", label: "Original Replacement Cost (TZS)", value: (item) => getOriginalReplacementCost(item) },
  { key: "unit_depreciated_price", label: "Unit DRC (TZS)", value: (item) => getUnitDepreciatedPrice(item) },
  { key: "total_depreciated_price", label: "Total DRC (TZS)", value: (item) => getUnitDepreciatedPrice(item) * getUnitCount(item) },
  { key: "pricing_status", label: "Pricing Status", value: (item) => getAssetValue(item, "pricing_status", "pricingStatus") },
  { key: "ward_name", label: "Ward", value: (item) => getAssetValue(item, "ward_name", "wardName") },
  { key: "institution_name", label: "Institution Name", value: (item) => getAssetValue(item, "institution_name", "institutionName") },
  { key: "other_building_name", label: "Other Building Name", value: (item) => getAssetValue(item, "other_building_name", "otherBuildingName") },
  { key: "institution_category", label: "Institution Category", value: (item) => getAssetValue(item, "institution_category", "institutionCategory") },
  { key: "institution_category_other", label: "Institution Category Other", value: (item) => getAssetValue(item, "institution_category_other", "institutionCategoryOther") },
  { key: "institution_other", label: "Institution Other", value: (item) => getAssetValue(item, "institution_other", "institutionOther") },
  { key: "department_name", label: "Department", value: (item) => getAssetValue(item, "department_name", "departmentName") },
  { key: "department_code", label: "Department Code", value: (item) => getAssetValue(item, "department_code", "departmentCode") },
  { key: "building_name", label: "Building Name", value: (item) => getAssetValue(item, "building_name", "buildingName") },
  { key: "floor", label: "Floor", value: (item) => item.floor },
  { key: "room_name_or_number", label: "Room Name / Number", value: (item) => getAssetValue(item, "room_name_or_number", "roomNameOrNumber") },
  { key: "room_others", label: "Room Others", value: (item) => getAssetValue(item, "room_others", "roomOthers") },
  { key: "barcode_image_url", label: "Barcode Image URL", value: (item) => getAssetValue(item, "barcode_image_url", "barcodeImageUrl") },
  { key: "first_photo_url", label: "Photo URL", value: (item) => getAssetValue(item, "first_photo_url", "firstPhotoUrl") },
  { key: "latitude", label: "Latitude", value: (item) => item.latitude },
  { key: "longitude", label: "Longitude", value: (item) => item.longitude },
  { key: "acquisition_date", label: "Acquisition Date", value: (item) => getAssetValue(item, "acquisition_date", "acquisitionDate") },
  { key: "useful_life_span_years", label: "Useful Life Span Years", value: (item) => getAssetValue(item, "useful_life_span_years", "usefulLifeSpanYears") },
  { key: "building_number", label: "Building Number", value: (item) => getAssetValue(item, "building_number", "buildingNumber") },
  { key: "plot_number", label: "Plot Number", value: (item) => getAssetValue(item, "plot_number", "plotNumber") },
  { key: "block_number", label: "Block Number", value: (item) => getAssetValue(item, "block_number", "blockNumber") },
  { key: "certificate_of_title", label: "Certificate of Title", value: (item) => getAssetValue(item, "certificate_of_title", "certificateOfTitle") },
  { key: "tenure_years", label: "Tenure Years", value: (item) => getAssetValue(item, "tenure_years", "tenureYears") },
  { key: "plot_size", label: "Plot Size", value: (item) => getAssetValue(item, "plot_size", "plotSize") },
  { key: "size_unit", label: "Size Unit", value: (item) => getAssetValue(item, "size_unit", "sizeUnit") },
  { key: "rate_per_unit", label: "Rate Per Unit", value: (item) => getAssetValue(item, "rate_per_unit", "ratePerUnit") },
  { key: "building_use_name", label: "Building Use", value: (item) => getAssetValue(item, "building_use_name", "buildingUseName") },
  { key: "year_of_manufacture", label: "Year of Manufacture", value: (item) => getAssetValue(item, "year_of_manufacture", "yearOfManufacture") },
  { key: "chassis_number", label: "Chassis Number", value: (item) => getAssetValue(item, "chassis_number", "chassisNumber") },
  { key: "engine_number", label: "Engine Number", value: (item) => getAssetValue(item, "engine_number", "engineNumber") },
  { key: "engine_capacity", label: "Engine Capacity", value: (item) => getAssetValue(item, "engine_capacity", "engineCapacity") },
  { key: "odo", label: "Odometer", value: (item) => item.odo },
  { key: "fuel_type", label: "Fuel Type", value: (item) => getAssetValue(item, "fuel_type", "fuelType") },
  { key: "imported_from", label: "Imported From", value: (item) => getAssetValue(item, "imported_from", "importedFrom") },
  { key: "imported_from_others", label: "Imported From Others", value: (item) => getAssetValue(item, "imported_from_others", "importedFromOthers") },
  { key: "body_type", label: "Body Type", value: (item) => getAssetValue(item, "body_type", "bodyType") },
  { key: "body_type_others", label: "Body Type Others", value: (item) => getAssetValue(item, "body_type_others", "bodyTypeOthers") },
  { key: "make", label: "Make", value: (item) => item.make },
  { key: "model", label: "Model", value: (item) => item.model },
  { key: "serial_number", label: "Serial Number", value: (item) => getAssetValue(item, "serial_number", "serialNumber") },
  { key: "measurement_unit", label: "Measurement Unit", value: (item) => getAssetValue(item, "measurement_unit", "measurementUnit") },
  { key: "measurement", label: "Measurement", value: (item) => item.measurement },
  { key: "has_water", label: "Has Water", value: (item) => getAssetValue(item, "has_water", "hasWater") },
  { key: "has_electricity", label: "Has Electricity", value: (item) => getAssetValue(item, "has_electricity", "hasElectricity") },
  { key: "has_site_work", label: "Has Site Work", value: (item) => getAssetValue(item, "has_site_work", "hasSiteWork") },
  { key: "right_of_occupance_start", label: "Right of Occupance Start", value: (item) => getAssetValue(item, "right_of_occupance_start", "rightOfOccupanceStart") },
  { key: "right_of_occupance_end", label: "Right of Occupance End", value: (item) => getAssetValue(item, "right_of_occupance_end", "rightOfOccupanceEnd") },
  { key: "client_name", label: "Client Name", value: (item) => getAssetValue(item, "client_name", "clientName") },
  { key: "project_name", label: "Project Name", value: (item) => getAssetValue(item, "project_name", "projectName") },
  { key: "valuer_name", label: "Valuer Name", value: (item) => getAssetValue(item, "valuer_name", "valuerName") },
  { key: "valuation_ref", label: "Valuation Ref", value: (item) => getAssetValue(item, "valuation_ref", "valuationRef") },
  { key: "valuation_status", label: "Valuation Status", value: (item) => getAssetValue(item, "valuation_status", "valuationStatus") },
  { key: "historical_cost", label: "Historical Cost", value: (item) => getAssetValue(item, "historical_cost", "historicalCost") },
  { key: "accumulated_depreciation_amount", label: "Accumulated Depreciation Amount", value: (item) => getAssetValue(item, "accumulated_depreciation_amount", "accumulatedDepreciationAmount") },
  { key: "fair_value", label: "Fair Value", value: (item) => getAssetValue(item, "fair_value", "fairValue") },
  { key: "net_book_value", label: "Net Book Value", value: (item) => getAssetValue(item, "net_book_value", "netBookValue") },
  { key: "remaining_useful_life_years", label: "Remaining Useful Life Years", value: (item) => getAssetValue(item, "remaining_useful_life_years", "remainingUsefulLifeYears") },
  { key: "cost_opening_balance", label: "Cost Opening Balance", value: (item) => getAssetValue(item, "cost_opening_balance", "costOpeningBalance") },
  { key: "cost_additions", label: "Cost Additions", value: (item) => getAssetValue(item, "cost_additions", "costAdditions") },
  { key: "cost_disposals", label: "Cost Disposals", value: (item) => getAssetValue(item, "cost_disposals", "costDisposals") },
  { key: "cost_closing_balance", label: "Cost Closing Balance", value: (item) => getAssetValue(item, "cost_closing_balance", "costClosingBalance") },
  { key: "depreciation_opening_balance", label: "Depreciation Opening Balance", value: (item) => getAssetValue(item, "depreciation_opening_balance", "depreciationOpeningBalance") },
  { key: "depreciation_charge", label: "Depreciation Charge", value: (item) => getAssetValue(item, "depreciation_charge", "depreciationCharge") },
  { key: "depreciation_disposals", label: "Depreciation Disposals", value: (item) => getAssetValue(item, "depreciation_disposals", "depreciationDisposals") },
  { key: "depreciation_closing_balance", label: "Depreciation Closing Balance", value: (item) => getAssetValue(item, "depreciation_closing_balance", "depreciationClosingBalance") },
  { key: "monthly_depreciation", label: "Monthly Depreciation", value: (item) => getAssetValue(item, "monthly_depreciation", "monthlyDepreciation") },
  { key: "is_land_surveyed", label: "Is Land Surveyed", value: (item) => getAssetValue(item, "is_land_surveyed", "isLandSurveyed") },
  { key: "registration_number", label: "Registration Number", value: (item) => getAssetValue(item, "registration_number", "registrationNumber") },
  { key: "is_reviewed", label: "Is Reviewed", value: (item) => getAssetValue(item, "is_reviewed", "isReviewed") },
  { key: "reviewer_name", label: "Reviewer Name", value: (item) => getAssetValue(item, "reviewer_name", "reviewerName") },
  { key: "review_status", label: "Review Status", value: (item) => getAssetValue(item, "review_status", "reviewStatus") },
  { key: "is_review_not_completed", label: "Is Review Not Completed", value: (item) => getAssetValue(item, "is_review_not_completed", "isReviewNotCompleted") },
  { key: "review_not_completed_remark", label: "Review Not Completed Remark", value: (item) => getAssetValue(item, "review_not_completed_remark", "reviewNotCompletedRemark") },
  { key: "created_at", label: "Created At", value: (item) => getAssetValue(item, "created_at", "createdAt") },
  { key: "updated_at", label: "Updated At", value: (item) => getAssetValue(item, "updated_at", "updatedAt") },
];

const NUMERIC_ASSET_EXPORT_COLUMNS = [
  "quantity",
  "number_of_buildings",
  "unit_count",
  "current_replacement_cost",
  "original_replacement_cost",
  "unit_depreciated_price",
  "total_depreciated_price",
  "tenure_years",
  "rate_per_unit",
  "year_of_manufacture",
  "engine_capacity",
  "odo",
  "historical_cost",
  "accumulated_depreciation_amount",
  "fair_value",
  "net_book_value",
  "cost_opening_balance",
  "cost_additions",
  "cost_disposals",
  "cost_closing_balance",
  "depreciation_opening_balance",
  "depreciation_charge",
  "depreciation_disposals",
  "depreciation_closing_balance",
  "monthly_depreciation",
];

const BASE_RESULT_COLUMNS = [
  { key: "barcode", label: "Barcode", render: (item) => item.barcode || "-" },
  { key: "category_name", label: "Category", render: (item) => item.category_name || item.categoryName || "-" },
  { key: "display_name", label: "Asset Name", render: (item) => item.display_name || item.displayName || item.name || "-" },
  { key: "institution_name", label: "Institution", render: (item) => item.institution_name || item.institutionName || item.institution_other || item.institutionOther || "-" },
  { key: "condition_name", label: "Condition", render: (item) => item.condition_name || item.conditionName || "-" },
  { key: "description", label: "Description", className: "pricing-description-cell", render: (item) => item.description || "-" },
  {
    key: "photo_gallery",
    label: "Photos",
    className: "pricing-photo-cell",
    render: (item, helpers = {}) => {
      const photoUrls = getPhotoUrls(item);
      if (photoUrls.length === 0) {
        return <span className="text-muted">No photo</span>;
      }
      const openPhotoPreview = helpers.openPhotoPreview || (() => {});

      return (
        <div className="pricing-photo-gallery">
          {photoUrls.slice(0, 4).map((photoUrl, index) => (
            <button
              type="button"
              key={`${photoUrl}-${index}`}
              title={`Preview image ${index + 1}`}
              className="pricing-photo-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openPhotoPreview(photoUrl, getAssetPhotoAlt(item, index));
              }}
            >
              <img src={photoUrl} alt={getAssetPhotoAlt(item, index)} className="pricing-photo-thumb" />
            </button>
          ))}
          {photoUrls.length > 4 ? (
            <span className="pricing-photo-more">+{photoUrls.length - 4}</span>
          ) : null}
        </div>
      );
    },
  },
  { key: "unit_count", label: "Units", render: (item) => item.unit_count ?? item.unitCount ?? item.quantity ?? 1 },
  {
    key: "pricing_user_name",
    label: "Priced By",
    render: (item) => item.pricing_user_name || item.pricingUserName || "-",
  },
  {
    key: "current_replacement_cost",
    label: "Current Replacement Cost",
    render: (item) => formatCurrency(getOriginalReplacementCost(item)),
  },
  {
    key: "total_current_replacement_cost",
    label: "Total Current Replacement Cost",
    render: (item) => {
      const unitCount = getUnitCount(item);
      const currentReplacementCost = getOriginalReplacementCost(item);
      return formatCurrency(unitCount * currentReplacementCost);
    },
  },
  {
    key: "unit_depreciated_price",
    label: "Unit DRC",
    render: (item) => formatCurrency(getUnitDepreciatedPrice(item)),
  },
  {
    key: "total_depreciated_price",
    label: "Total DRC",
    render: (item) => formatCurrency(getUnitDepreciatedPrice(item) * getUnitCount(item)),
  },
  {
    key: "pricing_status",
    label: "Status",
    render: (item) => (
      <Badge color={(item.pricing_status || item.pricingStatus) === "priced" ? "success" : "warning"}>
        {(item.pricing_status || item.pricingStatus) === "priced" ? "With Pricing" : "Without Pricing"}
      </Badge>
    ),
  },
];

const CATEGORY_RESULT_COLUMNS = {
  BLDG: [
    { key: "institution_name", label: "Institution", value: (item) => item.institution_name || item.institutionName },
    { key: "building_name", label: "Building", value: (item) => item.building_name || item.buildingName },
    { key: "building_number", label: "Building No.", value: (item) => item.building_number || item.buildingNumber },
    { key: "floor", label: "Floor", value: (item) => item.floor },
    { key: "room_name_or_number", label: "Room", value: (item) => item.room_name_or_number || item.roomNameOrNumber },
    { key: "number_of_buildings", label: "Buildings", value: (item) => item.number_of_buildings ?? item.numberOfBuildings },
    { key: "building_use_name", label: "Building Use", value: (item) => item.building_use_name || item.buildingUseName },
    { key: "plot_number", label: "Plot No.", value: (item) => item.plot_number || item.plotNumber },
    { key: "block_number", label: "Block No.", value: (item) => item.block_number || item.blockNumber },
    { key: "certificate_of_title", label: "Certificate", value: (item) => item.certificate_of_title || item.certificateOfTitle },
    { key: "plot_size", label: "Plot Size", value: (item) => item.plot_size || item.plotSize },
    { key: "size_unit", label: "Size Unit", value: (item) => item.size_unit || item.sizeUnit },
    { key: "rate_per_unit", label: "Rate/Unit", value: (item) => item.rate_per_unit || item.ratePerUnit },
  ],
  LAND: [
    { key: "plot_number", label: "Plot No.", value: (item) => item.plot_number || item.plotNumber },
    { key: "block_number", label: "Block No.", value: (item) => item.block_number || item.blockNumber },
    { key: "certificate_of_title", label: "Certificate", value: (item) => item.certificate_of_title || item.certificateOfTitle },
    { key: "tenure_years", label: "Tenure Years", value: (item) => item.tenure_years || item.tenureYears },
    { key: "plot_size", label: "Plot Size", value: (item) => item.plot_size || item.plotSize },
    { key: "size_unit", label: "Size Unit", value: (item) => item.size_unit || item.sizeUnit },
    { key: "rate_per_unit", label: "Rate/Unit", value: (item) => item.rate_per_unit || item.ratePerUnit },
    { key: "ward_name", label: "Ward", value: (item) => item.ward_name || item.wardName },
  ],
  VEH: [
    { key: "registration_number", label: "Registration No.", value: (item) => item.registration_number || item.registrationNumber },
    { key: "make", label: "Make", value: (item) => item.make },
    { key: "model", label: "Model", value: (item) => item.model },
    { key: "year_of_manufacture", label: "Manufacture Year", value: (item) => item.year_of_manufacture || item.yearOfManufacture },
    { key: "body_type", label: "Body Type", value: (item) => item.body_type || item.bodyType },
    { key: "fuel_type", label: "Fuel", value: (item) => item.fuel_type || item.fuelType },
    { key: "chassis_number", label: "Chassis No.", value: (item) => item.chassis_number || item.chassisNumber },
    { key: "engine_number", label: "Engine No.", value: (item) => item.engine_number || item.engineNumber },
    { key: "engine_capacity", label: "Engine Capacity", value: (item) => item.engine_capacity || item.engineCapacity },
    { key: "odo", label: "Odometer", value: (item) => item.odo },
  ],
  ICT: [
    { key: "make", label: "Make", value: (item) => item.make },
    { key: "model", label: "Model", value: (item) => item.model },
    { key: "serial_number", label: "Serial No.", value: (item) => item.serial_number || item.serialNumber },
    { key: "year_of_manufacture", label: "Manufacture Year", value: (item) => item.year_of_manufacture || item.yearOfManufacture },
    { key: "department_name", label: "Department", value: (item) => item.department_name || item.departmentName },
    { key: "room_name_or_number", label: "Room", value: (item) => item.room_name_or_number || item.roomNameOrNumber },
  ],
  EQP: [
    { key: "make", label: "Make", value: (item) => item.make },
    { key: "model", label: "Model", value: (item) => item.model },
    { key: "serial_number", label: "Serial No.", value: (item) => item.serial_number || item.serialNumber },
    { key: "year_of_manufacture", label: "Manufacture Year", value: (item) => item.year_of_manufacture || item.yearOfManufacture },
    { key: "measurement", label: "Measurement", value: (item) => item.measurement },
    { key: "measurement_unit", label: "Measurement Unit", value: (item) => item.measurement_unit || item.measurementUnit },
    { key: "department_name", label: "Department", value: (item) => item.department_name || item.departmentName },
    { key: "room_name_or_number", label: "Room", value: (item) => item.room_name_or_number || item.roomNameOrNumber },
  ],
  FURN: [
    { key: "make", label: "Make", value: (item) => item.make },
    { key: "model", label: "Model", value: (item) => item.model },
    { key: "measurement", label: "Measurement", value: (item) => item.measurement },
    { key: "measurement_unit", label: "Measurement Unit", value: (item) => item.measurement_unit || item.measurementUnit },
    { key: "department_name", label: "Department", value: (item) => item.department_name || item.departmentName },
    { key: "room_name_or_number", label: "Room", value: (item) => item.room_name_or_number || item.roomNameOrNumber },
  ],
  MACH: [
    { key: "make", label: "Make", value: (item) => item.make },
    { key: "model", label: "Model", value: (item) => item.model },
    { key: "serial_number", label: "Serial No.", value: (item) => item.serial_number || item.serialNumber },
    { key: "year_of_manufacture", label: "Manufacture Year", value: (item) => item.year_of_manufacture || item.yearOfManufacture },
    { key: "measurement", label: "Measurement", value: (item) => item.measurement },
    { key: "measurement_unit", label: "Measurement Unit", value: (item) => item.measurement_unit || item.measurementUnit },
  ],
  SITE: [
    { key: "measurement", label: "Measurement", value: (item) => item.measurement },
    { key: "measurement_unit", label: "Measurement Unit", value: (item) => item.measurement_unit || item.measurementUnit },
    { key: "has_water", label: "Has Water", value: (item) => item.has_water ?? item.hasWater },
    { key: "has_electricity", label: "Has Electricity", value: (item) => item.has_electricity ?? item.hasElectricity },
    { key: "has_site_work", label: "Has Site Work", value: (item) => item.has_site_work ?? item.hasSiteWork },
  ],
};

const PricingDashboard = () => {
  document.title = "Pricing | Valitan";
  const [searchParams] = useSearchParams();
  const initialPricingStatus = (() => {
    const value = String(searchParams.get("pricingStatus") || "").trim().toLowerCase();
    return ["all", "priced", "unpriced"].includes(value) ? value : "all";
  })();
  const shouldAutoSearch = searchParams.get("autoSearch") === "1";

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);
  const [descriptionOptions, setDescriptionOptions] = useState([]);
  const [filters, setFilters] = useState({
    assetCategoryId: "",
    name: [],
    description: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exportingResults, setExportingResults] = useState(false);
  const [exportingNamedResults, setExportingNamedResults] = useState(false);
  const [exportingAllAssetsNoPrice, setExportingAllAssetsNoPrice] = useState(false);
  const [exportingUsersDaily, setExportingUsersDaily] = useState(false);
  const [usersDailyDateRange, setUsersDailyDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 100,
    totalCount: 0,
    totalPages: 0,
  });
  const [, setStats] = useState({
    returnedCount: 0,
    pricedCount: 0,
    unpricedCount: 0,
  });
  const [progressSummary, setProgressSummary] = useState({
    categories: [],
    totalAssets: 0,
    pricedAssets: 0,
    unpricedAssets: 0,
    totalUnits: 0,
    pricedUnits: 0,
    unpricedUnits: 0,
  });
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [selectingAllResults, setSelectingAllResults] = useState(false);
  const [unitCurrentReplacementCost, setUnitCurrentReplacementCost] = useState("");
  const [photoPreview, setPhotoPreview] = useState({
    isOpen: false,
    url: "",
    title: "",
  });
  const [activeResultContext, setActiveResultContext] = useState(
    initialPricingStatus === "priced" || initialPricingStatus === "unpriced"
      ? initialPricingStatus
      : "unpriced"
  );

  const selectedAssetIdSet = useMemo(() => new Set(selectedAssetIds), [selectedAssetIds]);
  const selectedCount = selectedAssetIdSet.size;
  const resultCategoryCodes = useMemo(
    () => [...new Set(results.map((item) => String(item.category_code || item.categoryCode || "").trim()).filter(Boolean))],
    [results]
  );
  const dynamicResultColumns = useMemo(() => {
    if (resultCategoryCodes.length !== 1) {
      return [];
    }

    const configuredColumns = CATEGORY_RESULT_COLUMNS[resultCategoryCodes[0]] || [];
    return configuredColumns
      .filter((column) =>
        results.some((item) => hasExportValue(column.value(item)))
      )
      .map((column) => ({
        ...column,
        render: (item) => {
          const value = column.value(item);
          if (typeof value === "boolean") {
            return value ? "Yes" : "No";
          }
          return hasExportValue(value) ? value : "-";
        },
      }));
  }, [resultCategoryCodes, results]);
  const resultTableColumns = useMemo(
    () => [...BASE_RESULT_COLUMNS, ...dynamicResultColumns],
    [dynamicResultColumns]
  );
  const activeResults = results;
  const activeResultContextLabel = useMemo(
    () => RESULT_CONTEXTS.find((item) => item.value === activeResultContext)?.label || "Returned Assets",
    [activeResultContext]
  );
  const effectiveReturnedPricingStatus = activeResultContext;
  const openPhotoPreview = useCallback((url, title) => {
    setPhotoPreview({
      isOpen: true,
      url,
      title: title || "Asset photo",
    });
  }, []);
  const closePhotoPreview = useCallback(() => {
    setPhotoPreview({
      isOpen: false,
      url: "",
      title: "",
    });
  }, []);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const categoriesRes = await getAssetCategories(0, 1000);

      const categories = parseListPayload(categoriesRes);

      setCategoryOptions(
        categories.map((item) => ({
          value: item.id,
          label: item.name || item.code || "Unnamed Category",
        }))
      );
    } catch (error) {
      console.error("Failed to load pricing filter options:", error);
      enqueueSnackbar("Failed to load pricing filter options.", { variant: "error" });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const loadPricingProgress = useCallback(async () => {
    setLoadingProgress(true);
    try {
      const response = await getReplacementPricingProgress();
      const payload = response?.data?.data ?? {};
      setProgressSummary({
        categories: Array.isArray(payload.categories) ? payload.categories : [],
        totalAssets: payload.total_assets ?? 0,
        pricedAssets: payload.priced_assets ?? 0,
        unpricedAssets: payload.unpriced_assets ?? 0,
        totalUnits: payload.total_units ?? 0,
        pricedUnits: payload.priced_units ?? 0,
        unpricedUnits: payload.unpriced_units ?? 0,
      });
    } catch (error) {
      console.error("Failed to load pricing progress:", error);
      setProgressSummary({
        categories: [],
        totalAssets: 0,
        pricedAssets: 0,
        unpricedAssets: 0,
        totalUnits: 0,
        pricedUnits: 0,
        unpricedUnits: 0,
      });
    } finally {
      setLoadingProgress(false);
    }
  }, []);

  useEffect(() => {
    loadPricingProgress();
  }, [loadPricingProgress]);

  const loadDependentFilterOptions = useCallback(async (assetCategoryId, nameValue = [], pricingStatus = "unpriced") => {
    try {
      const response = await getReplacementPricingFilterOptions({
        assetCategoryId: assetCategoryId || undefined,
        name: nameValue?.length ? nameValue : undefined,
        pricingStatus: pricingStatus || undefined,
      });
      const payload = response?.data?.data ?? {};
      const nextNameOptions = Array.isArray(payload.names)
        ? payload.names.map((value) => ({ value, label: toUpperLabel(value) }))
        : [];
      const nextDescriptionOptions = Array.isArray(payload.descriptions)
        ? payload.descriptions.map((value) => ({ value, label: toTitleCaseLabel(value) }))
        : [];
      setNameOptions(nextNameOptions);
      setDescriptionOptions(nextDescriptionOptions);
      const allowedNameValues = new Set(nextNameOptions.map((option) => option.value));
      const allowedDescriptionValues = new Set(nextDescriptionOptions.map((option) => option.value));
      setFilters((current) => {
        const nextNames = current.name.filter((value) => allowedNameValues.has(value));
        const nextDescriptions = current.description.filter((value) => allowedDescriptionValues.has(value));
        if (nextNames.length === current.name.length && nextDescriptions.length === current.description.length) {
          return current;
        }
        return {
          ...current,
          name: nextNames,
          description: nextDescriptions,
        };
      });
    } catch (error) {
      console.error("Failed to load pricing filter options:", error);
      setNameOptions([]);
      setDescriptionOptions([]);
    }
  }, []);

  useEffect(() => {
    loadDependentFilterOptions(filters.assetCategoryId, filters.name, effectiveReturnedPricingStatus);
  }, [effectiveReturnedPricingStatus, filters.assetCategoryId, filters.name, loadDependentFilterOptions]);

  useEffect(() => {
    if (!shouldAutoSearch) return;
    fetchResults(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchResults = useCallback(async (pageNumber = 0, pageSize = pagination.size, pricingStatus = activeResultContext) => {
    setLoadingResults(true);
    try {
      const response = await getReplacementPricingAssets({
        ...filters,
        pricingStatus,
        page: pageNumber,
        size: pageSize,
      });
      const payload = parsePagePayload(response);
      const items = Array.isArray(payload.items) ? payload.items.map(normalizePricingItem) : [];
      setResults(items);
      setSelectedAssetIds([]);
      setStats({
        returnedCount: payload.returned_count ?? items.length,
        pricedCount: payload.priced_count ?? items.filter((item) => item.pricing_status === "priced").length,
        unpricedCount: payload.unpriced_count ?? items.filter((item) => item.pricing_status === "unpriced").length,
      });
      setPagination((current) => ({
        ...current,
        page: payload.page ?? pageNumber,
        size: pageSize,
        totalCount: payload.total_count ?? items.length,
        totalPages: payload.total_pages ?? 0,
      }));
    } catch (error) {
      console.error("Failed to load replacement pricing assets:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to load replacement pricing assets.",
        { variant: "error" }
      );
      setResults([]);
      setSelectedAssetIds([]);
    } finally {
      setLoadingResults(false);
    }
  }, [activeResultContext, filters, pagination.size]);

  const handleSearch = async (event) => {
    event?.preventDefault?.();
    await fetchResults(0);
  };

  const handleReset = () => {
    setFilters({
      assetCategoryId: "",
      name: [],
      description: [],
    });
    setResults([]);
    setSelectedAssetIds([]);
    setNameOptions([]);
    setDescriptionOptions([]);
    setUnitCurrentReplacementCost("");
    setStats({ returnedCount: 0, pricedCount: 0, unpricedCount: 0 });
    setPagination((current) => ({ ...current, page: 0, totalCount: 0, totalPages: 0 }));
    setActiveResultContext("unpriced");
  };

  const handleToggleRow = (assetId) => {
    const normalizedId = String(assetId || "").trim();
    if (!normalizedId) return;
    setSelectedAssetIds((current) => {
      return current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...new Set([...current, normalizedId])];
    });
  };

  const handleToggleAll = () => {
    if (activeResults.length === 0) return;
    const pageIds = [...new Set(activeResults.map((item) => resolveAssetId(item)).filter(Boolean))];
    const allSelected = pageIds.every((id) => selectedAssetIdSet.has(id));
    setSelectedAssetIds((current) => {
      if (allSelected) {
        return current.filter((id) => !pageIds.includes(id));
      }
      return [...new Set([...current, ...pageIds])];
    });
  };

  const handleSelectAllReturned = async () => {
    setSelectingAllResults(true);
    try {
      const pageSize = 500;
      let pageNumber = 0;
      let totalPages = 1;
      const allIds = [];

      while (pageNumber < totalPages) {
        const response = await getReplacementPricingAssets({
          ...filters,
          pricingStatus: effectiveReturnedPricingStatus,
          page: pageNumber,
          size: pageSize,
        });
        const payload = parsePagePayload(response);
        const items = Array.isArray(payload.items) ? payload.items : [];
        items.forEach((item) => {
          const assetId = resolveAssetId(item);
          if (assetId) {
            allIds.push(assetId);
          }
        });
        totalPages = payload.total_pages ?? payload.totalPages ?? 1;
        pageNumber += 1;
      }

      setSelectedAssetIds([...new Set(allIds)]);
    } catch (error) {
      console.error("Failed to select all returned assets:", error);
      enqueueSnackbar("Failed to select all returned assets.", { variant: "error" });
    } finally {
      setSelectingAllResults(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedAssetIds([]);
  };

  const handleApplyPricing = async () => {
    const amount = Number(unitCurrentReplacementCost);
    if (!selectedCount) {
      enqueueSnackbar("Select at least one asset first.", { variant: "warning" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      enqueueSnackbar("Enter a valid Unit Current Replacement Cost (TZS).", { variant: "warning" });
      return;
    }

    const confirmed = window.confirm(
      `Apply TZS ${amount.toLocaleString()} to ${selectedCount} selected asset(s)?`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const response = await applyReplacementPricing({
        assetIds: selectedAssetIds,
        unitCurrentReplacementCost: amount,
      });
      const payload = response?.data?.data ?? {};
      enqueueSnackbar(
        `Pricing applied to ${payload.updated_count ?? selectedCount} asset(s).`,
        { variant: "success" }
      );
      await fetchResults(pagination.page);
      await loadPricingProgress();
      setUnitCurrentReplacementCost("");
    } catch (error) {
      console.error("Failed to apply replacement pricing:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to apply replacement pricing.",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnitReplacementCostChange = (event) => {
    const normalizedValue = String(event?.target?.value ?? "").replace(/[^\d]/g, "");
    setUnitCurrentReplacementCost(normalizedValue);
  };

  const handleExportResults = async () => {
    setExportingResults(true);
    try {
      const pageSize = 500;
      let pageNumber = 0;
      let totalPages = 1;
      const allRows = [];

      while (pageNumber < totalPages) {
        const response = await getReplacementPricingAssets({
          ...filters,
          pricingStatus: effectiveReturnedPricingStatus,
          includeAllReviewStatuses: true,
          page: pageNumber,
          size: pageSize,
        });
        const payload = parsePagePayload(response);
        const items = Array.isArray(payload.items) ? payload.items : [];
        allRows.push(...items);
        totalPages = payload.total_pages ?? payload.totalPages ?? 1;
        pageNumber += 1;
      }

      if (allRows.length === 0) {
        enqueueSnackbar("No assets matched the current filters for export.", { variant: "warning" });
        return;
      }

      const exportRows = allRows.map((item) => ASSET_EXPORT_COLUMNS.reduce((row, column) => {
        row[column.key] = normalizeExportValue(column.value(item));
        return row;
      }, {}));
      const exportColumns = ASSET_EXPORT_COLUMNS
        .filter((column) => exportRows.some((row) => hasExportValue(row[column.key])))
        .map(({ key, label }) => ({ key, label }));
      const numericExportColumns = NUMERIC_ASSET_EXPORT_COLUMNS.filter((key) =>
        exportColumns.some((column) => column.key === key)
      );

      await exportToBrandedExcel(
        exportRows,
        exportColumns,
        `pricing-assets-${new Date().toISOString().split("T")[0]}.xlsx`,
        {
          sheetName: "Pricing",
          title: "TD Property",
          subtitle: "Asset valuation - Pricing",
          logoPath: tdLogo,
          numericColumns: numericExportColumns,
        }
      );
    } finally {
      setExportingResults(false);
    }
  };

  const handleExportAllAssetsNoPrice = async () => {
    setExportingAllAssetsNoPrice(true);
    try {
      const pageSize = 500;
      let pageNumber = 0;
      let totalPages = 1;
      const allRows = [];

      while (pageNumber < totalPages) {
        const response = await getReplacementPricingAssets({
          ...filters,
          pricingStatus: effectiveReturnedPricingStatus,
          includeAllReviewStatuses: true,
          page: pageNumber,
          size: pageSize,
        });
        const payload = parsePagePayload(response);
        const items = Array.isArray(payload.items) ? payload.items : [];
        allRows.push(...items);
        totalPages = payload.total_pages ?? payload.totalPages ?? 1;
        pageNumber += 1;
      }

      if (allRows.length === 0) {
        enqueueSnackbar("No assets matched the current filters for export.", { variant: "warning" });
        return;
      }

      const priceColumnKeys = new Set(["current_replacement_cost", "original_replacement_cost"]);
      const exportColumnsConfig = ASSET_EXPORT_COLUMNS.filter((column) => !priceColumnKeys.has(column.key));
      const exportRows = allRows.map((item) => exportColumnsConfig.reduce((row, column) => {
        row[column.key] = normalizeExportValue(column.value(item));
        return row;
      }, {}));
      const exportColumns = exportColumnsConfig
        .filter((column) => exportRows.some((row) => hasExportValue(row[column.key])))
        .map(({ key, label }) => ({ key, label }));
      const numericExportColumns = NUMERIC_ASSET_EXPORT_COLUMNS.filter((key) =>
        !priceColumnKeys.has(key) && exportColumns.some((column) => column.key === key)
      );

      await exportToBrandedExcel(
        exportRows,
        exportColumns,
        `pricing-assets-no-price-${new Date().toISOString().split("T")[0]}.xlsx`,
        {
          sheetName: "Pricing",
          title: "TD Property",
          subtitle: "Asset valuation - Pricing",
          logoPath: tdLogo,
          numericColumns: numericExportColumns,
          hyperlinkColumns: ["first_photo_url", "barcode_image_url"],
        }
      );
    } finally {
      setExportingAllAssetsNoPrice(false);
    }
  };

  const handleExportNamedResults = async () => {
    setExportingNamedResults(true);
    try {
      const responseFilters = {
        ...filters,
        pricingStatus: effectiveReturnedPricingStatus,
      };
      const response = await exportReplacementPricingNamedColumns(responseFilters);
      downloadBlobResponse(
        response,
        `pricing-assets-named-columns-${new Date().toISOString().split("T")[0]}.xlsx`
      );
    } catch (error) {
      console.error("Failed to export named pricing assets:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to export named pricing assets.",
        { variant: "error" }
      );
    } finally {
      setExportingNamedResults(false);
    }
  };

  const handleExportUsersDaily = async () => {
    if (!usersDailyDateRange.startDate || !usersDailyDateRange.endDate) {
      enqueueSnackbar("Select both start date and end date for users daily export.", { variant: "warning" });
      return;
    }
    if (usersDailyDateRange.endDate < usersDailyDateRange.startDate) {
      enqueueSnackbar("End date cannot be earlier than start date.", { variant: "warning" });
      return;
    }

    setExportingUsersDaily(true);
    try {
      const response = await exportReplacementPricingUsersDaily({
        startDate: usersDailyDateRange.startDate,
        endDate: usersDailyDateRange.endDate,
      });
      downloadBlobResponse(
        response,
        `pricing-users-daily-${new Date().toISOString().split("T")[0]}.xlsx`
      );
    } catch (error) {
      console.error("Failed to export users daily pricing summary:", error);
      const errorMessage = await resolveBlobErrorMessage(
        error,
        "Failed to export users daily pricing summary."
      );
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setExportingUsersDaily(false);
    }
  };

  const handleExportProgress = async () => {
      const exportRows = progressSummary.categories.map((item) => ({
        category_code: item.category_code || item.categoryCode || "-",
        category_name: item.category_name || item.categoryName || "-",
        total_assets: item.total_assets ?? item.totalAssets ?? 0,
        priced_assets: item.priced_assets ?? item.pricedAssets ?? 0,
        unpriced_assets: item.unpriced_assets ?? item.unpricedAssets ?? 0,
        total_units: item.total_units ?? item.totalUnits ?? 0,
        priced_units: item.priced_units ?? item.pricedUnits ?? 0,
        unpriced_units: item.unpriced_units ?? item.unpricedUnits ?? 0,
        progress_percent: `${item.progress_percent ?? item.progressPercent ?? 0}%`,
        unit_progress_percent: `${item.unit_progress_percent ?? item.unitProgressPercent ?? 0}%`,
      }));

    await exportToExcel(
      exportRows,
      [
        { key: "category_code", label: "Category Code" },
        { key: "category_name", label: "Category Name" },
        { key: "total_assets", label: "Total Assets" },
        { key: "priced_assets", label: "Priced Assets" },
        { key: "unpriced_assets", label: "Unpriced Assets" },
        { key: "total_units", label: "Total Units" },
        { key: "priced_units", label: "Priced Units" },
        { key: "unpriced_units", label: "Unpriced Units" },
        { key: "progress_percent", label: "Asset Progress %" },
        { key: "unit_progress_percent", label: "Unit Progress %" },
      ],
      `pricing-progress-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const allActiveRowsSelected = activeResults.length > 0 && activeResults
    .map((item) => resolveAssetId(item))
    .filter(Boolean)
    .every((assetId) => selectedAssetIdSet.has(assetId));

  return (
    <div className="page-content pricing-dashboard">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="Pricing" />


        <Card className="pricing-card mb-3">
          <CardHeader className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="card-title mb-1">Pricing Progress by Main Category</h4>
              <p className="text-muted mb-0">
                Total assets: <strong>{progressSummary.totalAssets}</strong>. Priced: <strong>{progressSummary.pricedAssets}</strong>. Unpriced: <strong>{progressSummary.unpricedAssets}</strong>.
                {" "}Total units: <strong>{progressSummary.totalUnits}</strong>. Priced units: <strong>{progressSummary.pricedUnits}</strong>. Unpriced units: <strong>{progressSummary.unpricedUnits}</strong>.
              </p>
            </div>
            <Button color="light" size="sm" onClick={handleExportProgress} disabled={loadingProgress || progressSummary.categories.length === 0}>
              Export Progress
            </Button>
          </CardHeader>
          <CardBody>
            {loadingProgress ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : progressSummary.categories.length === 0 ? (
              <Alert color="warning" className="mb-0">
                No pricing progress data is available.
              </Alert>
            ) : (
              <div className="pricing-table-wrap pricing-progress-wrap">
                <Table bordered hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Category Code</th>
                      <th>Category Name</th>
                      <th>Total Assets</th>
                      <th>Priced Assets</th>
                      <th>Unpriced Assets</th>
                      <th>Total Units</th>
                      <th>Priced Units</th>
                      <th>Unpriced Units</th>
                      <th>Asset Progress</th>
                      <th>Unit Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressSummary.categories.map((item, index) => (
                      <tr key={`${item.category_code || item.categoryCode || "category"}-${index}`}>
                        <td>{item.category_code || item.categoryCode || "-"}</td>
                        <td>{item.category_name || item.categoryName || "-"}</td>
                        <td>{item.total_assets ?? item.totalAssets ?? 0}</td>
                        <td>{item.priced_assets ?? item.pricedAssets ?? 0}</td>
                        <td>{item.unpriced_assets ?? item.unpricedAssets ?? 0}</td>
                        <td>{item.total_units ?? item.totalUnits ?? 0}</td>
                        <td>{item.priced_units ?? item.pricedUnits ?? 0}</td>
                        <td>{item.unpriced_units ?? item.unpricedUnits ?? 0}</td>
                        <td>{item.progress_percent ?? item.progressPercent ?? 0}%</td>
                        <td>{item.unit_progress_percent ?? item.unitProgressPercent ?? 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Row className="g-3">
          <Col xl={9}>
            <Card className="pricing-card">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">Filter Assets for Pricing</h4>
                  <p className="text-muted mb-0">
                    Filter the matching assets, then choose the assets that will be affected.
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                  {RESULT_CONTEXTS.map((context) => {
                    const isActive = activeResultContext === context.value;
                    return (
                      <Button
                        key={context.value}
                        type="button"
                        color={isActive ? context.tone : "light"}
                        outline={!isActive}
                        onClick={async () => {
                          setActiveResultContext(context.value);
                          setSelectedAssetIds([]);
                          await fetchResults(0, pagination.size, context.value);
                        }}
                      >
                        {context.label}
                      </Button>
                    );
                  })}
                  <Button color="secondary" size="sm" onClick={handleExportNamedResults} disabled={loadingResults || exportingNamedResults}>
                    {exportingNamedResults ? <><Spinner size="sm" className="me-2" />Exporting...</> : "Export Named Columns"}
                  </Button>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={typeof usersDailyDateRange.startDate === "string" ? usersDailyDateRange.startDate : ""}
                    onChange={(event) =>
                      setUsersDailyDateRange((current) => ({ ...current, startDate: event.target.value || "" }))
                    }
                    max={usersDailyDateRange.endDate || undefined}
                    style={{ width: 160 }}
                  />
                  <Input
                    type="date"
                    bsSize="sm"
                    value={typeof usersDailyDateRange.endDate === "string" ? usersDailyDateRange.endDate : ""}
                    onChange={(event) =>
                      setUsersDailyDateRange((current) => ({ ...current, endDate: event.target.value || "" }))
                    }
                    min={usersDailyDateRange.startDate || undefined}
                    style={{ width: 160 }}
                  />
                  <Button color="dark" size="sm" onClick={handleExportUsersDaily} disabled={loadingResults || exportingUsersDaily}>
                    {exportingUsersDaily ? <><Spinner size="sm" className="me-2" />Exporting...</> : "Export Users Daily"}
                  </Button>
                  <Button color="primary" size="sm" onClick={handleExportAllAssetsNoPrice} disabled={loadingResults || exportingAllAssetsNoPrice}>
                    {exportingAllAssetsNoPrice ? <><Spinner size="sm" className="me-2" />Exporting...</> : "Export All Assets No Price"}
                  </Button>
                  <Button color="success" size="sm" onClick={handleExportResults} disabled={loadingResults || exportingResults}>
                    {exportingResults ? <><Spinner size="sm" className="me-2" />Exporting...</> : "Export All Assets (Excel)"}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSearch}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Label className="form-label">Category</Label>
                      <Select
                        options={categoryOptions}
                        isLoading={loadingOptions}
                        isClearable
                        value={categoryOptions.find((option) => option.value === filters.assetCategoryId) || null}
                        onChange={(option) =>
                          setFilters((current) => ({
                            ...current,
                            assetCategoryId: option?.value || "",
                            name: [],
                            description: [],
                          }))
                        }
                        placeholder="All categories"
                      />
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Asset Name / Other Name</Label>
                      <Select
                        isMulti
                        options={nameOptions}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        isClearable
                        value={nameOptions.filter((option) => filters.name.includes(option.value))}
                        onChange={(options) =>
                          setFilters((current) => ({
                            ...current,
                            name: Array.isArray(options) ? options.map((option) => option.value) : [],
                            description: [],
                          }))
                        }
                        placeholder={
                          filters.assetCategoryId
                            ? "Select one or more asset names"
                            : "Select category first or search all"
                        }
                      />
                    </Col>
                    <Col md={12}>
                      <Label className="form-label">Description</Label>
                      <Select
                        isMulti
                        options={descriptionOptions}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option: CheckboxOption }}
                        isClearable
                        value={descriptionOptions.filter((option) => filters.description.includes(option.value))}
                        onChange={(options) =>
                          setFilters((current) => ({
                            ...current,
                            description: Array.isArray(options) ? options.map((option) => option.value) : [],
                          }))
                        }
                        placeholder={
                          filters.name.length > 0
                            ? "Select one or more descriptions"
                            : "Select asset name(s) first"
                        }
                      />
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Returned List Size</Label>
                      <Select
                        options={PAGE_SIZE_OPTIONS}
                        value={PAGE_SIZE_OPTIONS.find((option) => option.value === pagination.size) || PAGE_SIZE_OPTIONS[2]}
                        onChange={(option) => {
                          const selectedSize = option?.value || 100;
                          fetchResults(0, selectedSize);
                        }}
                        isSearchable={false}
                      />
                    </Col>
                    <Col xs={12} className="d-flex gap-2">
                      <Button color="primary" type="submit" disabled={loadingResults}>
                        {loadingResults ? <><Spinner size="sm" className="me-2" />Searching...</> : "Search Assets"}
                      </Button>
                      <Button color="light" type="button" onClick={handleReset}>
                        Reset
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>

            <Card className="pricing-card mt-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">Pricing Result List</h4>
                  <p className="text-muted mb-0">
                    Active tab: <strong>{activeResultContextLabel}</strong>. Assets in tab: <strong>{activeResults.length}</strong>. Selected assets to be affected: <strong>{selectedCount}</strong>.
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                  <Button color="secondary" size="sm" onClick={handleExportNamedResults} disabled={loadingResults || exportingNamedResults}>
                    {exportingNamedResults ? <><Spinner size="sm" className="me-2" />Exporting...</> : `Export ${activeResultContextLabel}`}
                  </Button>
                  <Button color="primary" size="sm" onClick={handleExportAllAssetsNoPrice} disabled={loadingResults || exportingAllAssetsNoPrice}>
                    {exportingAllAssetsNoPrice ? <><Spinner size="sm" className="me-2" />Exporting...</> : `Export ${activeResultContextLabel} No Price`}
                  </Button>
                  <Button color="success" size="sm" onClick={handleExportResults} disabled={loadingResults || exportingResults}>
                    {exportingResults ? <><Spinner size="sm" className="me-2" />Exporting...</> : `Export ${activeResultContextLabel} (Excel)`}
                  </Button>
                  <Button color="light" size="sm" onClick={handleToggleAll} disabled={activeResults.length === 0}>
                    {allActiveRowsSelected ? "Clear Page" : "Select Page"}
                  </Button>
                  <Button color="light" size="sm" onClick={handleSelectAllReturned} disabled={activeResults.length === 0 || selectingAllResults}>
                    {selectingAllResults ? <><Spinner size="sm" className="me-2" />Selecting...</> : "Select All Returned"}
                  </Button>
                  <Button color="light" size="sm" onClick={handleClearSelection} disabled={selectedCount === 0}>
                    Clear Selection
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loadingResults ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="text-muted mb-0 mt-3">Loading assets for pricing...</p>
                  </div>
                ) : results.length === 0 ? (
                  <Alert color="warning" className="mb-0">
                    No assets matched the current pricing filters.
                  </Alert>
                ) : (
                  <>
                    {activeResults.length === 0 ? (
                      <Alert color="light" className="mb-0">
                        No assets are available for the current pricing selection.
                      </Alert>
                    ) : (
                      <div className="pricing-table-wrap">
                        <Table bordered hover responsive className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 120 }}>Select</th>
                              {resultTableColumns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {activeResults.map((item, index) => {
                              const assetId = resolveAssetId(item);
                              const isSelected = assetId ? selectedAssetIdSet.has(assetId) : false;

                              return (
                                <tr
                                  key={item.rowId || `${activeResultContext}-pricing-row-${index}`}
                                  className={`pricing-selectable-row${isSelected ? " table-active" : ""}${!assetId ? " pricing-row-disabled" : ""}`}
                                  role="button"
                                  tabIndex={assetId ? 0 : -1}
                                  aria-pressed={isSelected}
                                  onClick={() => handleToggleRow(assetId)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      handleToggleRow(assetId);
                                    }
                                  }}
                                >
                                  <td>
                                    <Button
                                      type="button"
                                      color={isSelected ? "success" : "light"}
                                      size="sm"
                                      disabled={!assetId}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleToggleRow(assetId);
                                      }}
                                    >
                                      {isSelected ? "Selected" : "Select"}
                                    </Button>
                                  </td>
                                  {resultTableColumns.map((column) => (
                                    <td key={column.key} className={column.className || undefined}>
                                      {column.render(item, { openPhotoPreview })}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <Button
                        color="light"
                        disabled={pagination.page <= 0 || loadingResults}
                        onClick={() => fetchResults(Math.max(0, pagination.page - 1))}
                      >
                        Previous
                      </Button>
                      <div className="text-muted">
                        Page {pagination.page + 1} of {Math.max(pagination.totalPages, 1)} | Total assets {pagination.totalCount}
                      </div>
                      <Button
                        color="light"
                        disabled={loadingResults || pagination.page + 1 >= pagination.totalPages}
                        onClick={() => fetchResults(pagination.page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl={3}>
            <div className="pricing-sticky-panel">
              <Card className="pricing-card">
                <CardHeader>
                  <h4 className="card-title mb-1">Apply Pricing</h4>
                  <p className="text-muted mb-0">Enter one amount and apply it to the selected asset list only.</p>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label">Selected Assets</Label>
                    <div className="fw-semibold fs-5">{selectedCount}</div>
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">Unit Current Replacement Cost (TZS)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={formatThousands(unitCurrentReplacementCost)}
                      onChange={handleUnitReplacementCostChange}
                      placeholder="Enter amount"
                    />
                  </div>
                  <Alert color="light">
                    The submitted amount will only affect the currently selected assets on this page.
                  </Alert>
                  <Button
                    color="success"
                    className="w-100"
                    onClick={handleApplyPricing}
                    disabled={submitting || selectedCount === 0}
                  >
                    {submitting ? <><Spinner size="sm" className="me-2" />Applying...</> : "Apply to Selected Assets"}
                  </Button>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
        <Modal
          isOpen={photoPreview.isOpen}
          toggle={closePhotoPreview}
          centered
          size="xl"
          className="pricing-photo-preview-modal"
        >
          <ModalHeader toggle={closePhotoPreview}>
            {photoPreview.title || "Asset photo"}
          </ModalHeader>
          <ModalBody>
            {photoPreview.url ? (
              <img
                src={photoPreview.url}
                alt={photoPreview.title || "Asset photo"}
                className="pricing-photo-preview-image"
              />
            ) : (
              <div className="text-muted text-center py-4">No photo available.</div>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default PricingDashboard;
